import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardMedia,
  CardContent,
  IconButton,
  Modal,
  TextField,
  Button,
  Chip,
  Avatar,
  Divider,
  Tooltip,
  CardActions,
} from "@mui/material";
import {
  getSystemExplanationImages,
  getSystemExplanationVideos,
  updateExplanationImage,
  deleteExplanationImage,
  deleteExplanationVideo,
} from "../../../Services/SystemExplanationsApi";
import { API_BASE_URL_images } from "../../../Services/apiConfig";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import ImageIcon from "@mui/icons-material/Image";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import DescriptionIcon from "@mui/icons-material/Description";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

const groupVideosByExplanation = (videos) => {
  return videos.reduce((acc, video) => {
    const title = video.system_explanation?.title || "بدون شرح مرتبط";
    if (!acc[title]) {
      acc[title] = [];
    }
    acc[title].push(video);
    return acc;
  }, {});
};

const SystemExplanationsImagesVideosTap = () => {
  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loadingImages, setLoadingImages] = useState(true);
  const [loadingVideos, setLoadingVideos] = useState(true);
  const [errorImages, setErrorImages] = useState(null);
  const [errorVideos, setErrorVideos] = useState(null);
  const [editImageModalOpen, setEditImageModalOpen] = useState(false);
  const [selectedImageId, setSelectedImageId] = useState(null);
  const [editImageData, setEditImageData] = useState({
    title: "",
    description: "",
    image: null,
  });
  const [groupedVideos, setGroupedVideos] = useState({});

  // ✅ جلب الصور
  const fetchImages = async () => {
    try {
      const response = await getSystemExplanationImages();
      const data = response?.data?.data;
      console.log("📸 Images from API:", data);

      if (Array.isArray(data)) {
        setImages(data);
      } else {
        console.warn("Invalid images data format:", response);
        setImages([]);
      }
    } catch (error) {
      console.error("Error fetching images:", error);
      setErrorImages("فشل في جلب بيانات الصور");
    } finally {
      setLoadingImages(false);
    }
  };

  // ✅ جلب الفيديوهات
  const fetchVideos = async () => {
    try {
      const response = await getSystemExplanationVideos();
      const data = response?.data?.data;
      if (Array.isArray(data)) {
        setVideos(data);
        setGroupedVideos(groupVideosByExplanation(data)); // Group videos by explanation
      } else {
        setVideos([]);
        setGroupedVideos({});
      }
    } catch (error) {
      console.error("Error fetching videos:", error);
      setErrorVideos("فشل في جلب بيانات الفيديوهات");
    } finally {
      setLoadingVideos(false);
    }
  };

  useEffect(() => {
    fetchImages();
    fetchVideos();
  }, []);

  // ✅ تجميع الصور حسب الشرح
  const groupImagesByExplanation = (images) => {
    const grouped = images.reduce((acc, image) => {
      const title = image.system_explanation?.title || "بدون شرح مرتبط";
      if (!acc[title]) acc[title] = [];
      acc[title].push(image);
      return acc;
    }, {});
    return grouped;
  };

  // ✅ فتح مودال التعديل
  const handleEditImage = (image) => {
    setSelectedImageId(image.id);
    setEditImageData({
      title: image.title,
      description: image.description,
      image: null,
    });
    setEditImageModalOpen(true);
  };

  // ✅ التغيير في الحقول
  const handleEditImageChange = (e) => {
    const { name, value, files } = e.target;
    setEditImageData((prev) => ({
      ...prev,
      [name]: name === "image" ? files[0] : value,
    }));
  };

  // ✅ تنفيذ التحديث
  const handleEditImageSubmit = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append("title", editImageData.title);
      formData.append("description", editImageData.description);
      if (editImageData.image) {
        formData.append("image", editImageData.image);
      }

      const selectedImage = images.find((img) => img.id === selectedImageId);
      console.log("🧩 Selected Image Object:", selectedImage);

      // ✅ دعم الصور بدون system_explanation_id
      const systemExplanationId =
        selectedImage?.system_explanation_id ||
        selectedImage?.system_explanation?.id ||
        0; // ← fallback value

      formData.append("explanation_id", systemExplanationId);

      console.log("📝 explanation_id:", systemExplanationId);

      await updateExplanationImage(selectedImageId, formData);
      alert("✅ تم تعديل الصورة بنجاح!");
      setEditImageModalOpen(false);
      fetchImages();
    } catch (error) {
      console.error("❌ Error updating image:", error);
      alert(error.message || "فشل تعديل الصورة.");
    }
  };

  const handleEditImageModalClose = () => {
    setEditImageModalOpen(false);
    setSelectedImageId(null);
    setEditImageData({ title: "", description: "", image: null });
  };

  const handleDeleteImage = async (imageId) => {
    if (window.confirm("هل أنت متأكد أنك تريد حذف هذه الصورة؟")) {
      try {
        await deleteExplanationImage(imageId); // Call the delete function
        alert("✅ تم حذف الصورة بنجاح!");
        fetchImages(); // Refresh the images after deletion
      } catch (error) {
        console.error("❌ Error deleting image:", error);
        alert("فشل حذف الصورة.");
      }
    }
  };
  const extractYouTubeId = (url) => {
    const match = url.match(/(?:v=|\/)([0-9A-Za-z_-]{11})/);
    return match ? match[1] : "";
  };

  const handleEditVideo = (videoId) => {
    console.log(`Editing video with ID: ${videoId}`);
    // Add edit logic here
  };

  const handleDeleteVideo = async (videoId) => {
    if (window.confirm("هل أنت متأكد أنك تريد حذف هذا الفيديو؟")) {
      try {
        await deleteExplanationVideo(videoId); // Call the delete function
        alert("✅ تم حذف الفيديو بنجاح!");
        fetchVideos(); // Refresh the videos after deletion
      } catch (error) {
        console.error("❌ Error deleting video:", error);
        alert("فشل حذف الفيديو.");
      }
    }
  };

  return (
    <Box>
      {/* قسم الصور */}
      <Box sx={{ mb: 5 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            mb: 3,
            p: 2,
            bgcolor: "#e3f2fd",
            borderRadius: 2,
          }}
        >
          <Avatar sx={{ bgcolor: "#1976d2", width: 50, height: 50 }}>
            <PhotoLibraryIcon sx={{ fontSize: 28 }} />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h5"
              sx={{ fontWeight: "bold", color: "#1976d2" }}
            >
              معرض الصور
            </Typography>
            <Typography variant="body2" color="text.secondary">
              إدارة وتعديل صور الشروحات
            </Typography>
          </Box>
          {!loadingImages && !errorImages && (
            <Chip
              label={`${images.length} صورة`}
              color="primary"
              sx={{ fontWeight: "bold" }}
            />
          )}
        </Box>

        {loadingImages ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 8,
            }}
          >
            <CircularProgress size={60} />
            <Typography variant="h6" sx={{ ml: 2 }}>
              جاري تحميل الصور...
            </Typography>
          </Box>
        ) : errorImages ? (
          <Alert
            severity="error"
            sx={{ fontSize: "1.1rem", py: 3, borderRadius: 2 }}
          >
            {errorImages}
          </Alert>
        ) : images.length > 0 ? (
          Object.entries(groupImagesByExplanation(images)).map(
            ([title, groupedImages]) => (
              <Box key={title} sx={{ mb: 4 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 2,
                  }}
                >
                  <DescriptionIcon color="primary" />
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: "bold", color: "#333" }}
                  >
                    {title}
                  </Typography>
                  <Chip
                    label={`${groupedImages.length}`}
                    size="small"
                    color="info"
                  />
                </Box>
                <Grid container spacing={3}>
                  {groupedImages.map((image) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={image.id}>
                      <Card
                        elevation={3}
                        sx={{
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          transition: "all 0.3s",
                          borderRadius: 2,
                          overflow: "hidden",
                          "&:hover": {
                            transform: "translateY(-8px)",
                            boxShadow: 8,
                          },
                        }}
                      >
                        <CardMedia
                          component="img"
                          height="200"
                          image={`${API_BASE_URL_images}/${image.image}`}
                          alt={image.title}
                          sx={{
                            objectFit: "cover",
                            borderBottom: "3px solid #1976d2",
                          }}
                        />
                        <CardContent sx={{ flexGrow: 1, p: 2 }}>
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: "bold",
                              mb: 1,
                              minHeight: 48,
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}
                          >
                            {image.title}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              minHeight: 40,
                            }}
                          >
                            {image.description}
                          </Typography>
                        </CardContent>
                        <Divider />
                        <CardActions
                          sx={{
                            p: 1.5,
                            bgcolor: "#fafafa",
                            justifyContent: "center",
                          }}
                        >
                          <Tooltip title="تعديل الصورة">
                            <IconButton
                              color="primary"
                              onClick={() => handleEditImage(image)}
                              sx={{
                                bgcolor: "#e3f2fd",
                                "&:hover": { bgcolor: "#bbdefb" },
                              }}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="حذف الصورة">
                            <IconButton
                              color="error"
                              onClick={() => handleDeleteImage(image.id)}
                              sx={{
                                bgcolor: "#ffebee",
                                "&:hover": { bgcolor: "#ffcdd2" },
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </CardActions>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )
          )
        ) : (
          <Box
            sx={{
              textAlign: "center",
              py: 8,
              bgcolor: "#f5f5f5",
              borderRadius: 2,
            }}
          >
            <ImageIcon sx={{ fontSize: 80, color: "#bdbdbd", mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              لا توجد صور متوفرة
            </Typography>
          </Box>
        )}
      </Box>

      <Divider sx={{ my: 5 }} />

      {/* قسم الفيديوهات */}
      <Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            mb: 3,
            p: 2,
            bgcolor: "#f3e5f5",
            borderRadius: 2,
          }}
        >
          <Avatar sx={{ bgcolor: "#9c27b0", width: 50, height: 50 }}>
            <VideoLibraryIcon sx={{ fontSize: 28 }} />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h5"
              sx={{ fontWeight: "bold", color: "#9c27b0" }}
            >
              مكتبة الفيديوهات
            </Typography>
            <Typography variant="body2" color="text.secondary">
              إدارة وتعديل فيديوهات الشروحات
            </Typography>
          </Box>
          {!loadingVideos && !errorVideos && (
            <Chip
              label={`${videos.length} فيديو`}
              color="secondary"
              sx={{ fontWeight: "bold" }}
            />
          )}
        </Box>

        {loadingVideos ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 8,
            }}
          >
            <CircularProgress size={60} />
            <Typography variant="h6" sx={{ ml: 2 }}>
              جاري تحميل الفيديوهات...
            </Typography>
          </Box>
        ) : errorVideos ? (
          <Alert
            severity="error"
            sx={{ fontSize: "1.1rem", py: 3, borderRadius: 2 }}
          >
            {errorVideos}
          </Alert>
        ) : Object.keys(groupedVideos).length > 0 ? (
          Object.entries(groupedVideos).map(([title, videos]) => (
            <Box key={title} sx={{ mb: 4 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 2,
                }}
              >
                <PlayCircleOutlineIcon color="secondary" />
                <Typography
                  variant="h6"
                  sx={{ fontWeight: "bold", color: "#333" }}
                >
                  {title}
                </Typography>
                <Chip
                  label={`${videos.length}`}
                  size="small"
                  color="secondary"
                />
              </Box>
              <Grid container spacing={3}>
                {videos.map((video) => (
                  <Grid item xs={12} sm={6} md={4} key={video.id}>
                    <Card
                      elevation={3}
                      sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        transition: "all 0.3s",
                        borderRadius: 2,
                        overflow: "hidden",
                        "&:hover": {
                          transform: "translateY(-8px)",
                          boxShadow: 8,
                        },
                      }}
                    >
                      <Box sx={{ position: "relative" }}>
                        <CardMedia
                          component="iframe"
                          height="240"
                          src={`https://www.youtube.com/embed/${extractYouTubeId(
                            video.url
                          )}`}
                          title={video.title}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          sx={{ border: "none" }}
                        />
                      </Box>
                      <CardContent sx={{ flexGrow: 1, p: 2 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 1,
                          }}
                        >
                          <PlayCircleOutlineIcon color="secondary" fontSize="small" />
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: "bold",
                              flex: 1,
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}
                          >
                            {video.title}
                          </Typography>
                        </Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            minHeight: 40,
                          }}
                        >
                          {video.description}
                        </Typography>
                      </CardContent>
                      <Divider />
                      <CardActions
                        sx={{
                          p: 1.5,
                          bgcolor: "#fafafa",
                          justifyContent: "center",
                        }}
                      >
                        <Tooltip title="تعديل الفيديو">
                          <IconButton
                            color="primary"
                            onClick={() => handleEditVideo(video.id)}
                            sx={{
                              bgcolor: "#e3f2fd",
                              "&:hover": { bgcolor: "#bbdefb" },
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="حذف الفيديو">
                          <IconButton
                            color="error"
                            onClick={() => handleDeleteVideo(video.id)}
                            sx={{
                              bgcolor: "#ffebee",
                              "&:hover": { bgcolor: "#ffcdd2" },
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))
        ) : (
          <Box
            sx={{
              textAlign: "center",
              py: 8,
              bgcolor: "#f5f5f5",
              borderRadius: 2,
            }}
          >
            <VideoLibraryIcon sx={{ fontSize: 80, color: "#bdbdbd", mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              لا توجد فيديوهات متوفرة
            </Typography>
          </Box>
        )}
      </Box>

      {/* Modal تعديل الصورة */}
      <Modal
        open={editImageModalOpen}
        onClose={handleEditImageModalClose}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Card
          sx={{
            width: "90%",
            maxWidth: 600,
            borderRadius: 2,
            maxHeight: "90vh",
            overflow: "auto",
          }}
        >
          <Box
            sx={{
              bgcolor: "#1976d2",
              color: "white",
              p: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <ImageIcon />
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                تعديل الصورة
              </Typography>
            </Box>
            <IconButton
              onClick={handleEditImageModalClose}
              sx={{ color: "white" }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <CardContent sx={{ p: 3 }}>
            <form onSubmit={handleEditImageSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="عنوان الصورة"
                    name="title"
                    value={editImageData.title}
                    onChange={handleEditImageChange}
                    fullWidth
                    required
                    variant="outlined"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="الوصف"
                    name="description"
                    value={editImageData.description}
                    onChange={handleEditImageChange}
                    fullWidth
                    required
                    multiline
                    rows={3}
                    variant="outlined"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    component="label"
                    fullWidth
                    startIcon={<CloudUploadIcon />}
                    sx={{
                      py: 2,
                      borderStyle: "dashed",
                      borderWidth: 2,
                    }}
                  >
                    تحميل صورة جديدة
                    <input
                      type="file"
                      name="image"
                      hidden
                      onChange={handleEditImageChange}
                      accept="image/*"
                    />
                  </Button>
                  {editImageData.image && (
                    <Typography
                      variant="caption"
                      color="success.main"
                      sx={{ mt: 1, display: "block" }}
                    >
                      ✓ تم اختيار الصورة: {editImageData.image.name}
                    </Typography>
                  )}
                </Grid>

                <Grid item xs={12} sx={{ mt: 2 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    size="large"
                    sx={{ py: 1.5, fontWeight: "bold" }}
                  >
                    تحديث الصورة
                  </Button>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </Card>
      </Modal>
    </Box>
  );
};

export default SystemExplanationsImagesVideosTap;
